import { zodResolver } from '@hookform/resolvers/zod'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import {
  emptyResponseSchema,
  securityErrorKey,
  useSensitiveRequest,
} from '@/features/security/securityClient'
import type { forgotPasswordType } from '@/utils/schemas'
import { forgotPasswordSchema } from '@/utils/schemas'

export const ForgotPasswordForm = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const form = useForm<forgotPasswordType>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  const {
    handleSubmit,
    control,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors = {} },
  } = form

  const request = useSensitiveRequest()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const lock = useRef(false)

  const recoverPasswordRequest = async (email: string) => {
    if (lock.current) return
    lock.current = true
    setIsLoading(true)
    setError('')
    try {
      await request('/api/v1/auth/recover-password', emptyResponseSchema, {
        body: { email },
      })
      toast.success(t('dashboard.landingForgotPassword.emailSend'))
      navigate(AppRoutes.forgotPasswordCheckYourInbox)
    } catch (e) {
      setError(securityErrorKey(e))
    } finally {
      lock.current = false
      setIsLoading(false)
    }
  }

  const onSubmit = (data: any) => {
    recoverPasswordRequest(data.email)
  }

  return (
    <div className="flex flex-col gap-8 w-full max-w-[436px]">
      <div className="flex flex-col gap-5">
        <p className="text-[40px] font-bold tracking-wide">
          {t('dashboard.landingForgotPassword.forgotPassword')}
        </p>
        <p className="text-1xl text-textAccent tracking-tight">
          {t('dashboard.landingForgotPassword.forgotPasswordText')}
        </p>
      </div>

      <Form {...form}>
        {error && (
          <p role="alert" className="text-dynamicRed">
            {t(error)}
          </p>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full bg-white rounded-md">
                <FormControl className="w-full focus:outline-none">
                  <Input
                    {...field}
                    placeholder={t('dashboard.landingForgotPassword.example')}
                  />
                </FormControl>
                <FormMessage data-name="email" />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="py-3 px-5 font-medium tracking-widest h-11"
            isLoading={isLoading}
          >
            {t('dashboard.landingForgotPassword.continue')}
          </Button>
        </form>
      </Form>
    </div>
  )
}
